import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Progress,
  Select,
  Input,
  Button,
  Space,
  message,
} from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';

const { Search } = Input;
const { Option } = Select;

export default function BroadcastDashboard() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    smtp_provider: 'all',
    server: 'all',
    zone: 'all',
    search: '',
  });
  const [sorting, setSorting] = useState({
    field: 'created_at',
    order: 'descend',
  });
  const [globalStats, setGlobalStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [smtpProviders, setSmtpProviders] = useState([]);
  const [servers, setServers] = useState([]);
  const [zones, setZones] = useState([]);
  const [allZones, setAllZones] = useState([]);

  const token = localStorage.getItem('prae.jwt');

  // Fetch metadata pour les filtres
  const fetchMetadata = useCallback(async () => {
    try {
      const [providersRes, filterOptionsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/smtp-providers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        fetch(`${process.env.REACT_APP_API_URL}/broadcasts/filter-options`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      ]);

      if (providersRes.ok) {
        const providers = await providersRes.json();
        // Filtrer les providers supprimés
        setSmtpProviders(providers.filter(p => !p.deleted_at));
      }

      if (filterOptionsRes.ok) {
        const options = await filterOptionsRes.json();
        setCategories(options.categories || []);
        setServers(options.servers || []);
        setAllZones(options.zones || []);
        setZones(options.zones || []);
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  }, [token]);

  // Fetch broadcasts avec filtres, pagination et tri
  const fetchBroadcasts = useCallback(
    async (page = 1, pageSize = 20) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          pageSize: pageSize.toString(),
          sortBy: sorting.field,
          sortOrder: sorting.order === 'ascend' ? 'ASC' : 'DESC',
          ...filters,
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/broadcasts/dashboard?${params}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch broadcasts');
        }

        const data = await response.json();
        setBroadcasts(data.broadcasts);
        setGlobalStats(data.globalStats);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
        }));
      } catch (error) {
        console.error('Error fetching broadcasts:', error);
        message.error('Erreur lors du chargement des broadcasts');
      } finally {
        setLoading(false);
      }
    },
    [filters, sorting.field, sorting.order, token]
  );

  // Gestion des filtres
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };

      if (key === 'server') {
        if (value === 'all') {
          setZones(allZones);
          newFilters.zone = 'all';
        } else {
          fetchZonesForServer(value);
          newFilters.zone = 'all';
        }
      }

      return newFilters;
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Récupérer les zones pour un serveur spécifique
  const fetchZonesForServer = async server => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/broadcasts/filter-options?server=${server}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.ok) {
        const data = await response.json();
        setZones(data.zones || []);
      }
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  // Gestion du changement de pagination/tri
  const handleTableChange = (paginationInfo, filters, sorter) => {
    console.log('🔍 handleTableChange appelé:', { sorter });

    // Mise à jour du tri
    if (sorter.field) {
      console.log('✅ Mise à jour du tri:', sorter.field, sorter.order);
      setSorting({
        field: sorter.field,
        order: sorter.order || 'descend',
      });
    }

    // Mise à jour de la pagination
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  // Status renderers
  const renderStatus = status => {
    const statusConfig = {
      active: { color: 'processing', text: 'Actif' },
      paused: { color: 'warning', text: 'En pause' },
      completed: { color: 'success', text: 'Terminé' },
      new: { color: 'default', text: 'Nouveau' },
      deleted: { color: 'error', text: 'Supprimé' },
    };

    const config = statusConfig[status] || statusConfig.new;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const formatDate = dateString => {
    if (!dateString) return '-';
    return DateTime.fromISO(dateString)
      .setZone('Europe/Paris')
      .toFormat('dd/MM/yyyy HH:mm');
  };

  // Colonnes du tableau avec tri
  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: true,
      sortOrder: sorting.field === 'name' ? sorting.order : null,
      render: (name, record) => (
        <div>
          <Link
            to={`/broadcasts/${record.id}`}
            className="font-medium text-blue-600 hover:text-blue-800"
          >
            {name}
          </Link>
          {record.description && (
            <div className="text-sm text-gray-500 mt-1">
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: true,
      sortOrder: sorting.field === 'status' ? sorting.order : null,
      render: renderStatus,
    },
    {
      title: 'Catégorie',
      dataIndex: 'category_label',
      key: 'category',
      width: 120,
      sorter: true,
      sortOrder: sorting.field === 'category' ? sorting.order : null,
      render: label => label || '-',
    },
    {
      title: 'Serveur',
      dataIndex: 'smtp_provider_server',
      key: 'server',
      width: 100,
      sorter: true,
      sortOrder: sorting.field === 'server' ? sorting.order : null,
      render: server => server || '-',
    },
    {
      title: 'Zone',
      dataIndex: 'smtp_provider_zone',
      key: 'zone',
      width: 200,
      sorter: true,
      sortOrder: sorting.field === 'zone' ? sorting.order : null,
      render: zone => zone || '-',
    },
    {
      title: 'Progression du jour',
      dataIndex: 'progress_today', // ✅ AJOUT DU dataIndex
      key: 'progress_today',
      width: 180,
      // ✅ FONCTION DE COMPARAISON au lieu de "true"
      sorter: (a, b) => {
        const aVal = a.progress_today || 0;
        const bVal = b.progress_today || 0;
        console.log('📊 Tri progress_today:', {
          aVal,
          bVal,
          diff: aVal - bVal,
        });
        return aVal - bVal;
      },
      sortOrder: sorting.field === 'progress_today' ? sorting.order : null,
      render: (progress_today, record) => (
        <div>
          <Progress
            percent={progress_today || 0}
            size="small"
            status={progress_today >= 90 ? 'success' : 'normal'}
          />
          <div className="text-xs text-gray-500 mt-1">
            {(record.emails_today || 0).toLocaleString()} /{' '}
            {(record.quota_today || 0).toLocaleString()}
          </div>
        </div>
      ),
    },
    {
      title: 'Calendrier',
      dataIndex: 'current_day', // ✅ AJOUT DU dataIndex
      key: 'current_day',
      width: 120,
      // ✅ FONCTION DE COMPARAISON au lieu de "true"
      sorter: (a, b) => {
        const aVal = a.current_day || 0;
        const bVal = b.current_day || 0;
        console.log('📅 Tri current_day:', { aVal, bVal, diff: aVal - bVal });
        return aVal - bVal;
      },
      sortOrder: sorting.field === 'current_day' ? sorting.order : null,
      render: (current_day, record) => (
        <div className="text-center">
          <div className="font-semibold text-blue-600">
            Jour {current_day || 0}
          </div>
          <div className="text-xs text-gray-500">
            / {record.warmup_total_days || 0} jours
          </div>
        </div>
      ),
    },
    {
      title: 'Progression totale',
      dataIndex: 'progress_total', // ✅ AJOUT DU dataIndex
      key: 'progress_total',
      width: 180,
      // ✅ FONCTION DE COMPARAISON au lieu de "true"
      sorter: (a, b) => {
        const aVal = a.progress_total || 0;
        const bVal = b.progress_total || 0;
        console.log('📈 Tri progress_total:', {
          aVal,
          bVal,
          diff: aVal - bVal,
        });
        return aVal - bVal;
      },
      sortOrder: sorting.field === 'progress_total' ? sorting.order : null,
      render: (progress_total, record) => (
        <div>
          <Progress
            percent={progress_total || 0}
            size="small"
            status={
              record.health_status === 'critical' ? 'exception' : 'normal'
            }
          />
          <div className="text-xs text-gray-500 mt-1">
            {(record.sent_emails || 0).toLocaleString()} /{' '}
            {(record.total_planned || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400">Total prévu warmup</div>
        </div>
      ),
    },
    {
      title: 'Dernier envoi',
      dataIndex: 'last_sent_at',
      key: 'last_sent_at',
      width: 140,
      render: formatDate,
    },
  ];

  // Effects
  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  useEffect(() => {
    console.log('🔄 Effect fetchBroadcasts:', { sorting });
    fetchBroadcasts(pagination.current, pagination.pageSize);
  }, [fetchBroadcasts, pagination.current, pagination.pageSize]);

  return (
    <div className="p-6 mt-12 bg-gray-50 min-h-screen">
      <div className="w-full">
        {/* Header avec stats globales */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Tableau de bord Broadcasts
          </h1>

          <Row gutter={16}>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Total Broadcasts"
                  value={globalStats.total_broadcasts || 0}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Actifs"
                  value={globalStats.active_broadcasts || 0}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Terminés"
                  value={globalStats.completed_broadcasts || 0}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Emails aujourd'hui"
                  value={globalStats.emails_sent_today || 0}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Total envoyés"
                  value={globalStats.total_sent_emails || 0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="En pause"
                  value={globalStats.paused_broadcasts || 0}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>
        </div>

        {/* Filtres */}
        <Card className="mb-6">
          <Row gutter={16} align="middle">
            <Col span={5}>
              <Search
                placeholder="Rechercher..."
                allowClear
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                onSearch={value => handleFilterChange('search', value)}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col span={3}>
              <Select
                placeholder="Statut"
                value={filters.status}
                onChange={value => handleFilterChange('status', value)}
                style={{ width: '100%' }}
              >
                <Option value="all">Tous les statuts</Option>
                <Option value="active">Actif</Option>
                <Option value="paused">En pause</Option>
                <Option value="completed">Terminé</Option>
                <Option value="new">Nouveau</Option>
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="Serveur"
                value={filters.server}
                onChange={value => handleFilterChange('server', value)}
                style={{ width: '100%' }}
              >
                <Option value="all">Tous les serveurs</Option>
                {servers.map(server => (
                  <Option key={server} value={server}>
                    {server}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={5}>
              <Select
                placeholder="Zone"
                value={filters.zone}
                onChange={value => handleFilterChange('zone', value)}
                style={{ width: '100%' }}
                disabled={filters.server === 'all'}
              >
                <Option value="all">Toutes les zones</Option>
                {zones.map(zone => (
                  <Option key={zone} value={zone}>
                    {zone}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Catégorie"
                value={filters.category}
                onChange={value => handleFilterChange('category', value)}
                style={{ width: '100%' }}
              >
                <Option value="all">Toutes les catégories</Option>
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.id}>
                    {cat.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() =>
                    fetchBroadcasts(pagination.current, pagination.pageSize)
                  }
                  loading={loading}
                >
                  Actualiser
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Tableau */}
        <Card>
          <Table
            columns={columns}
            dataSource={broadcasts}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} broadcasts`,
            }}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ x: 'max-content', y: 600 }}
            sticky
            size="small"
          />
        </Card>
      </div>
    </div>
  );
}
